import styled from 'styled-components'
import { COLORS, SPACING, BORDER_RADIUS, TYPOGRAPHY, FONT_WEIGHT } from '../../../../styles/theme'
import { OpenResponsesView } from '../../../survey/components/insights/OpenResponsesView'
import type { InterviewQuestion } from '@bandos/shared'

export interface InterviewInsightCardProps {
  question: InterviewQuestion
  questionIndex: number
  answers: (string | number | boolean | string[] | null)[]
  isPrinting: boolean
  isDeleted?: boolean
}

const Card = styled.div<{ $deleted?: boolean }>`
  background: var(--color-bg-card);
  border: 1px solid var(--color-border);
  border-radius: ${BORDER_RADIUS.lg};
  padding: ${SPACING.xl};
  page-break-inside: avoid;
  overflow: hidden;
  opacity: ${(p) => (p.$deleted ? 0.75 : 1)};
`

const DeletedBadge = styled.span`
  font-size: ${TYPOGRAPHY.caption.fontSize};
  font-weight: ${FONT_WEIGHT.semibold};
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: ${COLORS.gray500};
  background: ${COLORS.gray100};
  padding: 2px ${SPACING.sm};
  border-radius: ${BORDER_RADIUS.full};
  white-space: nowrap;
`

const CardHeader = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: ${SPACING.md};
  margin-bottom: ${SPACING.lg};
`

const QuestionMeta = styled.div`
  flex: 1;
`

const QuestionNumber = styled.span`
  font-size: ${TYPOGRAPHY.caption.fontSize};
  font-weight: ${FONT_WEIGHT.semibold};
  color: var(--color-text-secondary);
  text-transform: uppercase;
  letter-spacing: 0.5px;
`

const QuestionText = styled.h3`
  font-size: ${TYPOGRAPHY.caption.fontSize};
  font-weight: ${FONT_WEIGHT.semibold};
  color: var(--color-text-primary);
  margin: ${SPACING.xs} 0 0 0;
  line-height: 1.4;
`

export function InterviewInsightCard({
  question,
  questionIndex,
  answers,
  isPrinting,
  isDeleted = false,
}: InterviewInsightCardProps) {
  // Notes are stored as JSONB and may have legacy non-string entries.
  // Coerce/filter to the strings the responses view expects.
  const textAnswers = answers
    .map((a) => {
      if (a === null) return ''
      if (typeof a === 'string') return a
      if (Array.isArray(a)) return a.join(', ')
      return String(a)
    })
    .filter((a) => a.length > 0)

  return (
    <Card $deleted={isDeleted}>
      <CardHeader>
        <QuestionMeta>
          {!isDeleted && <QuestionNumber>Q{questionIndex + 1}</QuestionNumber>}
          <QuestionText>{question.question}</QuestionText>
        </QuestionMeta>
        {isDeleted && <DeletedBadge>Deleted</DeletedBadge>}
      </CardHeader>

      <OpenResponsesView answers={textAnswers} isPrinting={isPrinting} />
    </Card>
  )
}
